import dynamic from "next/dynamic";
import Link from "next/link";
import Head from "next/head";
import { useState } from "react";

import Footer from "../components/Footer";
import ContactUsHome from "../components/homepage/ContactUsHome";
import { CustomerSection } from "../components/ai-agent-demo/CustomerSection";
import BlogSlider from "../components/reusable/BlogSlider";

const Navbar = dynamic(() => import("../components/Navbar"), { ssr: false });

/* ─── data ──────────────────────────────────────────────── */

const flagshipWork = [
  {
    metric: ">90%",
    headline: "Reduction in technology support effort",
    descriptor: "Orchestrik deployment — managed services",
    body: "We built and deployed Orchestrik agents into our own managed services operation — triaging incidents, executing routine remediation, and escalating with full context. Support effort reduced by over 90%.",
    link: "/orchestrik",
    linkLabel: "See Orchestrik",
    external: false,
  },
  {
    metric: "97%",
    headline: "Reduction in SKU identification time",
    descriptor: "Custom computer vision — global supply chain leader",
    body: "Computer vision system identifying 100,000+ SKUs across 35 countries. Sub-3-minute lookup per item, built as a custom convolutional neural network with serverless inference — running in production.",
    link: "/industry/supply-chain",
    linkLabel: "Supply chain work",
    external: false,
  },
  {
    metric: "3 days → 1 hour",
    headline: "Third-party risk analysis turnaround",
    descriptor: "Research agent — global intelligence and risk consulting firm",
    body: "Four AI models orchestrated to automate third-party risk reports — document discovery, financial analysis, regulatory checks, and synthesis. Analyses that took 3 days now complete in under an hour.",
    link: "/blog/business-intelligence-ai-strategy",
    linkLabel: "Case study",
    external: false,
  },
];

const comparisonRows = [
  {
    label: "Trigger",
    rpa: "Fixed rule or schedule",
    ml: "Batch data or API call",
    agentic: "Monitors context, self-triggers on goal state",
  },
  {
    label: "Decision-making",
    rpa: "Pre-defined decision tree",
    ml: "Statistical model output",
    agentic: "Multi-step reasoning across tools and data sources",
  },
  {
    label: "Adaptability",
    rpa: "Breaks on new inputs — needs reprogramming",
    ml: "Retrain required for distribution shift",
    agentic: "In-context adaptation; escalates novel cases to humans",
  },
  {
    label: "Failure handling",
    rpa: "Error + halt",
    ml: "Silent degradation",
    agentic: "Audit trail, rollback, human escalation paths built in",
  },
];

const industryUseCases = [
  {
    name: "Banking & Fintech",
    workflows: [
      "Compliance monitoring — RBI Digital Lending, SEBI",
      "Fraud detection on transaction streams",
      "Tech-stack audit agents for due diligence",
      "Portfolio analytics for capital markets",
    ],
    link: "/industry/banking",
  },
  {
    name: "Supply Chain & Logistics",
    workflows: [
      "Computer vision for SKU identification at scale",
      "Multi-site inventory reconciliation",
      "Demand forecasting agents",
      "ERP integration — Dynamics 365, SAP",
    ],
    link: "/industry/supply-chain",
  },
  {
    name: "Insurance",
    workflows: [
      "AI underwriting decision support",
      "Claims automation",
      "IRDAI compliance + IIB data reporting",
      "Cloud-native core insurance infrastructure",
    ],
    link: "/industry/insurance",
  },
  {
    name: "Life Sciences",
    workflows: [
      "Formulation assistants — CDSCO + FDA 21 CFR Part 11",
      "Regulatory document automation",
      "R&D research agents",
      "Clinical and research data pipelines",
    ],
    link: "/industry/life-sciences",
  },
  {
    name: "Retail & D2C",
    workflows: [
      "Personalisation engines",
      "Demand forecasting and inventory agents",
      "Returns and fraud detection",
      "Last-mile delivery optimisation",
    ],
    link: "/industry/retail",
  },
  {
    name: "Hospitality",
    workflows: [
      "Guest concierge agents",
      "Revenue management agents",
      "Ops and maintenance routing",
      "Booking-system orchestration across channels",
    ],
    link: "/blog/agentic-ai-hospitality",
  },
  {
    name: "Professional Services & Accounting",
    workflows: [
      "Month-end close reconciliation agents",
      "Audit working-paper preparation",
      "Tax research — Indian GST + global",
      "Engagement-letter drafting and review",
    ],
    link: null,
  },
  {
    name: "Quick Commerce",
    workflows: [
      "Demand-forecasting on 10-minute delivery SKUs",
      "Dark-store inventory rebalancing",
      "Dynamic-pricing for hyperlocal demand",
      "Delivery-failure triage agents",
    ],
    link: "/industry/retail",
  },
];

const architecturePatterns = [
  {
    pattern: "Single-agent + tools",
    whenToUse: "Bounded task within one decision domain",
    whenNotTo: "Multi-step workflows crossing system boundaries",
    example: "Tax research agent — retrieves, analyses, and drafts across jurisdictions in one run",
  },
  {
    pattern: "Multi-agent orchestration",
    whenToUse: "Workflows where specialists must hand off: research → analysis → drafting → review",
    whenNotTo: "Where a single agent with tools would suffice — coordination overhead is real",
    example: "Third-party risk (4 agents): discovery → financial analysis → regulatory checks → synthesis",
  },
  {
    pattern: "Agentic RAG",
    whenToUse: "Decisions requiring citation of source documents; compliance and audit contexts",
    whenNotTo: "Open-ended generative tasks without a retrievable ground truth",
    example: "Compliance monitoring across RBI Digital Lending circulars — cited, traceable answers",
  },
  {
    pattern: "Decision-support + HITL",
    whenToUse: "Regulated decisions (credit, claims, clinical) requiring human sign-off",
    whenNotTo: "High-volume, low-stakes tasks where a human-in-loop creates a bottleneck",
    example: "AI underwriting decision support — prepares recommendation, flags edge cases, human confirms",
  },
];

const productionRequirements = [
  {
    number: "01",
    title: "Deterministic test harness",
    body: "Non-deterministic agents are tested against golden-trace replays and expected-outcome distributions before go-live. Every agent has a test suite that validates the decision path, not just the final output.",
  },
  {
    number: "02",
    title: "Observability + tracing",
    body: "Every agent call captures span, latency, token count, tool calls made, and intermediate reasoning steps. We instrument before go-live, not after an incident.",
  },
  {
    number: "03",
    title: "Audit trail",
    body: "Regulator-grade log of inputs, outputs, and decisions — retained per the applicable framework: RBI, IRDAI, FDA 21 CFR Part 11. Not optional in regulated industries.",
  },
  {
    number: "04",
    title: "Rollback paths",
    body: "Every production agent can be disabled or rolled back to the last known-good configuration without disrupting the underlying operational system.",
  },
  {
    number: "05",
    title: "Non-determinism monitoring",
    body: "Drift detection against expected outcome distributions. Alerts fire when behaviour shifts outside acceptable bounds — treated as a production incident, not an accepted limitation.",
  },
];

const methodology = [
  {
    step: "01",
    title: "Architecture sprint",
    body: "Two weeks, fixed price. We identify the right agent pattern — single-agent, multi-agent orchestration, or hybrid with human-in-loop. We scope data access, integration surface, and failure modes before writing a line of agent code.",
  },
  {
    step: "02",
    title: "Production build",
    body: "Agents that operate inside your infrastructure — ERP, SaaS, legacy APIs — with monitoring, audit trails, and rollback paths. Production-grade code, not a prototype. Tested for non-deterministic behaviour before go-live.",
  },
  {
    step: "03",
    title: "Operate & improve",
    body: "Post-go-live monitoring with edge cases logged and models updated. Non-deterministic behaviour tracked against expected outcomes. 4–8 week warranty period included.",
  },
];

const evaluationCriteria = [
  {
    ask: "“Show me an agent running in production today.”",
    why: "A demo proves a prompt works. Production proves the integration, the error handling, and the monitoring work. Ask for the deployment date and who operates it now.",
    ours: "Computer vision across 100,000+ SKUs in 35 countries, a four-model research agent for a global risk consulting firm, and Orchestrik agents running inside our own managed services operation.",
  },
  {
    ask: "“Which agent pattern would you use, and why not the other three?”",
    why: "A provider who names an architecture before understanding your workflow is selling what they already built. The pattern should follow the failure modes, not the sales deck.",
    ours: "We choose between single-agent with tools, multi-agent orchestration, agentic RAG, and decision-support with a human in the loop during the architecture sprint, and we write down why the others were rejected.",
  },
  {
    ask: "“What happens when the agent is wrong?”",
    why: "Every agent is wrong sometimes. The question is whether that is detected, logged, reversible, and escalated to a person, or whether it quietly corrupts a workflow for a week.",
    ours: "Audit trails, rollback paths, and human escalation ship in the first release, not after the first incident. Non-deterministic behaviour is monitored against expected outcome distributions.",
  },
  {
    ask: "“Who owns the regulatory posture?”",
    why: "Where RBI, SEBI, IRDAI, CDSCO, or DPDP exposure exists, compliance is an architecture decision. A provider who treats it as a review at the end will need to rebuild.",
    ours: "The architecture sprint produces a compliance posture document before the production build starts.",
  },
  {
    ask: "“How is the engagement bounded and priced?”",
    why: "Open-ended retainers transfer all the schedule risk to you. You should know the shape of the commitment before the first invoice.",
    ours: "A two-week fixed-price architecture sprint comes first. The production build is scoped and priced after it, so you know the full cost before committing to it.",
  },
  {
    ask: "“What happens after go-live?”",
    why: "Handover without observability tooling and runbooks is abandonment. Ask what is covered, for how long, and at whose cost.",
    ours: "A 4 to 8 week warranty on every engagement, covering edge-case triage, model and prompt updates, and rollback if a regression appears. After that, managed operations or a full handover.",
  },
];

const regulatoryAnchors = [
  {
    title: "RBI Digital Lending + SEBI",
    body: "Fintech agent deployments aligned with RBI Digital Lending Guidelines and SEBI cybersecurity circulars from the architecture phase, not retrofit.",
  },
  {
    title: "DPDP consent + data residency",
    body: "Indian enterprise agent deployments built around DPDP consent requirements and Indian data residency from the outset.",
  },
  {
    title: "IRDAI digital transformation",
    body: "Insurance agent workflows designed against IRDAI directives and IIB data reporting mandates.",
  },
  {
    title: "FDA 21 CFR Part 11 / CDSCO",
    body: "Life sciences automation built for FDA Part 11 audit trails and CDSCO compliance requirements.",
  },
  {
    title: "Public sector data sovereignty",
    body: "Government and public-sector deployments architected for data residency, classification handling, and audit posture.",
  },
];

const infoBlogs = {
  basics: [
    { title: "Agentic AI vs Automation — A CXO's Guide", slug: "agentic-ai-vs-automation" },
    { title: "What Decision Makers Should Know About Agentic AI in 2025", slug: "what-decision-makers-should-know-about-agentic-ai-in-2025" },
  ],
  industry: [
    { title: "Agentic AI in Hospitality: Use Cases & Impact", slug: "agentic-ai-hospitality" },
    { title: "Unleashing Agentic AI: Supply Chain, Fintech and Pharma", slug: "unleashing-agentic-ai-transforming-supply-chain-fintech-and-pharma" },
  ],
  approach: [
    { title: "Solution Architecture using Agentic AI", slug: "solution-architecture-using-agentic-ai" },
    { title: "Testing Better with Agentic AI", slug: "agentic-ai-in-software-testing" },
  ],
};

const relatedBlogs = [
  { title: "Agentic AI vs Automation — A CXO's Guide", slug: "agentic-ai-vs-automation", img: "/blogImages/hero/agentic-ai-vs-automation.webp" },
  { title: "Agentic AI in Hospitality: Use Cases & Impact", slug: "agentic-ai-hospitality", img: "/blogImages/hero/agentic-ai-hos.webp" },
  { title: "Solution Architecture using Agentic AI", slug: "solution-architecture-using-agentic-ai", img: "/blogImages/hero/solution-architecture-agentic-ai.webp" },
  { title: "What Decision Makers Should Know About Agentic AI in 2025", slug: "what-decision-makers-should-know-about-agentic-ai-in-2025", img: "/blogImages/hero/decision-makers-2025.webp" },
  { title: "Unleashing Agentic AI: Supply Chain, Fintech and Pharma", slug: "unleashing-agentic-ai-transforming-supply-chain-fintech-and-pharma", img: "/blogImages/hero/Unleashing-Agentic-AI-Transforming-Supply-Chain-Fintech-and-Pharma.webp" },
  { title: "Agentic AI Pilot Success", slug: "agentic-ai-pilot-success", img: "/blogImages/hero/agentic-ai-pilot.webp" },
];

const faqItems = [
  {
    question: "What outcomes has ITMTB shipped with agentic AI?",
    answer:
      "ITMTB has deployed a custom CNN for SKU identification across 100,000+ SKUs in 35 countries (97% reduction in ops time), a 4-model research agent for a global risk consulting firm (3 days to 1 hour for third-party analyses), and Orchestrik agents into managed services operations (over 90% reduction in technology support effort). All three are in production and actively monitored.",
  },
  {
    question: "How does ITMTB handle Indian regulatory compliance for AI agents?",
    answer:
      "Compliance is built into the architecture phase, not retrofitted. We design fintech agents around RBI Digital Lending Guidelines and SEBI; insurance agents around IRDAI and IIB; life sciences automation for FDA 21 CFR Part 11 and CDSCO; and Indian enterprise deployments around DPDP consent and data residency. The architecture sprint produces a compliance posture document before the production build starts.",
  },
  {
    question: "How long does an agentic AI engagement take?",
    answer:
      "Engagements begin with a two-week fixed-price architecture sprint. From there, a bounded production agent typically ships in 6–14 weeks — simple single-agent deployments land closer to 6 weeks; multi-agent orchestration across enterprise systems is typically 10–14 weeks. Every engagement includes a 4–8 week warranty period post-handoff. The sprint is fixed-price; the production build is scoped and priced after the sprint.",
  },
  {
    question: "How can I know if my organisation is ready for agentic AI?",
    answer:
      "ITMTB's Agentic AI Readiness Assessment identifies which workflows are ready, which need infrastructure work first, and what a bounded pilot looks like. The 3-minute quiz produces a structured report based on six readiness dimensions: data infrastructure, process maturity, technical compatibility, organisational alignment, regulatory posture, and change management.",
  },
  {
    question: "How does agentic AI work in accounting and professional services?",
    answer:
      "Accounting involves highly structured, document-heavy, repetitive cognition — the ideal surface for agentic AI. We deploy agents for month-end close reconciliation (ingesting GL data, flagging discrepancies, producing a variance report), audit working-paper preparation, GST and international tax research, and engagement-letter drafting and review. The agent pattern is typically agentic RAG — retrieving your chart of accounts, prior-period files, and regulatory sources, then reasoning across them. Accuracy is verified against expected outputs before go-live.",
  },
  {
    question: "What is the difference between an agentic AI and an LLM chatbot?",
    answer:
      "A chatbot responds to a prompt and waits for the next one. An agentic AI agent takes a goal, plans a sequence of steps, calls tools and APIs, checks its own output, and iterates — autonomously, without prompting each step. The practical difference: a chatbot tells you what is in a contract; an agent reads the contract, queries three compliance databases, flags discrepancies against your template, and drafts a response — in one run. Agents use an LLM as a reasoning engine, not a chat interface.",
  },
  {
    question: "How long does a production agent take to ship?",
    answer:
      "The two-week architecture sprint produces a scoped build plan. From there, production timelines depend on integration complexity, not model complexity. A bounded single-agent deployment typically ships in 6 weeks from sprint completion. Multi-agent orchestration across enterprise systems (ERP, legacy APIs, multiple data sources) is typically 10–14 weeks. You know the full build cost before committing to the production phase.",
  },
  {
    question: "What does ITMTB's AI agent warranty cover?",
    answer:
      "Every engagement includes a 4–8 week post-go-live warranty. During this period we monitor agent behaviour against expected outcome distributions, triage production edge cases, update models or prompts as required, and cover rollback if a regression is detected. Non-deterministic behaviour caught post-launch is treated as a delivery issue, not a new engagement. After warranty, we offer ongoing managed operations or hand over with full observability tooling and runbooks.",
  },
  {
    question: "What does an agentic AI services company actually do?",
    answer:
      "An agentic AI services company designs, builds, and operates AI agents that carry out multi-step work inside your existing systems, rather than supplying a model or a chat interface. In practice that means selecting the agent architecture, connecting it to your ERP, CRM, and legacy APIs, building the test harness and observability that make it safe to run unattended, and staying on after go-live. The distinction that matters when comparing providers is whether they stop at the demo or stay through production and operations.",
  },
  {
    question: "How do I evaluate agentic AI service providers?",
    answer:
      "Ask six questions. Show me an agent in production today, not a demo. Which agent pattern would you use, and why not the alternatives. What happens when the agent is wrong. Who owns the regulatory posture. How is the engagement bounded and priced. What happens after go-live. Most of these are architecture and operations questions rather than model questions, which is where agent projects actually fail. Providers who can only answer the first one have shipped pilots, not production systems.",
  },
  {
    question: "What is the difference between agentic AI consulting and agentic AI development?",
    answer:
      "Agentic AI consulting answers what to build and whether it is worth building: workflow selection, ROI ranking, build versus buy, and a pilot scope. Agentic AI development builds and operates the agent itself: architecture, integration, testing, monitoring, and support. ITMTB does both, but they are separate tracks. If you already know which workflow you want automated, start with the two-week architecture sprint described on this page. If you are still deciding where AI fits, start with the executive diagnostic on our AI strategy for business page.",
  },
];

/* ─── schema ─────────────────────────────────────────────── */

const schemaGraph = [
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://www.itmtb.com" },
      { "@type": "ListItem", position: 2, name: "Agentic AI Services", item: "https://www.itmtb.com/agentic-ai" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "@id": "https://www.itmtb.com/agentic-ai#webpage",
    url: "https://www.itmtb.com/agentic-ai",
    name: "Agentic AI Services & Solutions | ITMTB",
    description:
      "ITMTB designs, builds, and operates production agentic AI systems. Computer vision for 100k+ SKUs (97% reduction), research agents collapsing 3-day analyses to 1 hour, Orchestrik tech-support automation with 90%+ reduction. Built for Indian regulatory compliance from architecture.",
    publisher: {
      "@type": "Organization",
      name: "ITMTB",
      url: "https://www.itmtb.com",
      logo: { "@type": "ImageObject", url: "https://www.itmtb.com/logo.png" },
      sameAs: ["https://www.linkedin.com/company/itmtb/", "https://twitter.com/ItmtbT"],
    },
    mainEntity: {
      "@type": "Service",
      name: "Agentic AI Services & Solutions",
      provider: { "@type": "Organization", name: "ITMTB", url: "https://www.itmtb.com" },
      areaServed: { "@type": "Place", name: "India and Global" },
      serviceType: [
        "Agentic AI Services",
        "Agentic AI Consulting",
        "Agentic AI Development",
        "Enterprise AI Agent Deployment",
      ],
    },
  },
  {
    "@context": "https://schema.org",
    "@type": "HowTo",
    name: "How ITMTB builds production agentic AI systems",
    step: [
      {
        "@type": "HowToStep",
        position: 1,
        name: "Architecture sprint",
        text: "Two weeks, fixed price. We identify the right agent pattern, scope data access, integration surface, and failure modes before writing a line of agent code.",
      },
      {
        "@type": "HowToStep",
        position: 2,
        name: "Production build",
        text: "Agents built to operate inside your infrastructure — ERP, SaaS, legacy APIs — with monitoring, audit trails, and rollback paths. Production-grade code, not a prototype.",
      },
      {
        "@type": "HowToStep",
        position: 3,
        name: "Operate & improve",
        text: "Post-go-live monitoring with edge cases logged and models updated. Non-deterministic behaviour tracked against expected outcomes. 4–8 week warranty period included.",
      },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map((item) => ({
      "@type": "Question",
      name: item.question,
      acceptedAnswer: { "@type": "Answer", text: item.answer },
    })),
  },
];

/* ─── sub-components ─────────────────────────────────────── */

const SectionEyebrow = ({ label }) => (
  <p className="text-amber-700 text-xs font-semibold uppercase tracking-widest mb-3">{label}</p>
);

const FaqAccordion = ({ items }) => {
  const [open, setOpen] = useState(null);
  return (
    <div>
      {items.map((item, i) => (
        <div key={i} className="border-b border-slate-200">
          <button
            onClick={() => setOpen(open === i ? null : i)}
            className="w-full flex justify-between items-start gap-4 text-left py-5 focus:outline-none group"
            aria-expanded={open === i}
          >
            <span className="text-base font-semibold text-slate-900 leading-snug">{item.question}</span>
            <span className="flex-shrink-0 text-slate-400 group-hover:text-slate-600 transition-colors text-xl leading-none mt-0.5">
              {open === i ? "−" : "+"}
            </span>
          </button>
          <div className={`pb-5 ${open === i ? "block" : "hidden"}`}>
            <p className="text-slate-600 text-sm leading-relaxed">{item.answer}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─── hero illustration ──────────────────────────────────── */

const AgentPipelineSVG = () => (
  <svg viewBox="0 0 440 360" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full max-w-md opacity-60">
    {/* grid dots */}
    {[...Array(5)].map((_, row) =>
      [...Array(7)].map((_, col) => (
        <circle key={`${row}-${col}`} cx={30 + col * 64} cy={40 + row * 68} r="1.5" fill="#334155" />
      ))
    )}
    {/* horizontal flow line */}
    <line x1="40" y1="165" x2="400" y2="165" stroke="#1e293b" strokeWidth="1"/>
    {/* agent 1 → orchestrator connector */}
    <path d="M 174 165 L 216 165" stroke="#334155" strokeWidth="1.5"/>
    <polygon points="216,160 226,165 216,170" fill="#334155"/>
    {/* orchestrator → agent 2 connector */}
    <path d="M 284 165 L 316 165" stroke="#334155" strokeWidth="1.5"/>
    <polygon points="316,160 326,165 316,170" fill="#334155"/>
    {/* input arrow */}
    <path d="M 52 165 L 118 165" stroke="#334155" strokeWidth="1.5"/>
    <polygon points="118,160 128,165 118,170" fill="#334155"/>
    {/* output arrow */}
    <path d="M 370 165 L 405 165" stroke="#334155" strokeWidth="1.5"/>
    <polygon points="405,160 415,165 405,170" fill="#334155"/>
    {/* tool branches from agent 1 */}
    <line x1="150" y1="189" x2="120" y2="252" stroke="#1e293b" strokeWidth="1.5"/>
    <line x1="150" y1="189" x2="180" y2="252" stroke="#1e293b" strokeWidth="1.5"/>
    {/* tool branches from orchestrator */}
    <line x1="250" y1="195" x2="220" y2="265" stroke="#334155" strokeWidth="1.5"/>
    <line x1="250" y1="195" x2="280" y2="265" stroke="#334155" strokeWidth="1.5"/>
    {/* tool branch from agent 2 */}
    <line x1="342" y1="189" x2="342" y2="252" stroke="#1e293b" strokeWidth="1.5"/>
    {/* agent 1 */}
    <circle cx="150" cy="165" r="24" stroke="#475569" strokeWidth="1.5"/>
    <circle cx="150" cy="165" r="8" stroke="#475569" strokeWidth="1"/>
    {/* agent 2 */}
    <circle cx="342" cy="165" r="24" stroke="#475569" strokeWidth="1.5"/>
    <circle cx="342" cy="165" r="8" stroke="#475569" strokeWidth="1"/>
    {/* orchestrator — amber accent */}
    <circle cx="250" cy="165" r="34" stroke="#d97706" strokeWidth="2"/>
    <circle cx="250" cy="165" r="14" stroke="#d97706" strokeWidth="1.5" strokeOpacity="0.5"/>
    {/* tool nodes */}
    <circle cx="120" cy="268" r="14" stroke="#334155" strokeWidth="1.5"/>
    <circle cx="180" cy="268" r="14" stroke="#334155" strokeWidth="1.5"/>
    <circle cx="220" cy="282" r="14" stroke="#475569" strokeWidth="1.5"/>
    <circle cx="280" cy="282" r="14" stroke="#475569" strokeWidth="1.5"/>
    <circle cx="342" cy="268" r="14" stroke="#334155" strokeWidth="1.5"/>
    {/* input/output markers */}
    <rect x="18" y="152" width="34" height="26" rx="3" stroke="#1e293b" strokeWidth="1.5"/>
    <rect x="406" y="152" width="34" height="26" rx="3" stroke="#1e293b" strokeWidth="1.5"/>
  </svg>
);

/* ─── page ───────────────────────────────────────────────── */

const AiAgentHomePage = () => (
  <div className="w-screen bg-white text-slate-900">
    <Navbar />

    <Head>
      <title>Agentic AI Services & Solutions | ITMTB</title>
      <meta
        name="description"
        content="ITMTB designs, builds, and operates production agentic AI systems. Computer vision for 100k+ SKUs, research agents collapsing 3-day analyses to 1 hour, Orchestrik tech-support automation with 90%+ reduction. Built for Indian regulatory compliance from architecture."
      />
      <meta
        name="keywords"
        content="agentic ai services, agentic ai for accounting, agentic ai advisor, ai analytics software, ai agents for enterprise, agentic ai india, agentic ai consulting, enterprise ai agents, agentic ai quick commerce, production ai agents"
      />
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Agentic AI Services & Solutions | ITMTB" />
      <meta
        property="og:description"
        content="Production agentic AI built for Indian regulatory compliance. Three named outcomes in production: 97% SKU ops reduction, 3 days to 1 hour for risk analysis, 90%+ support effort reduction."
      />
      <meta property="og:image" content="https://www.itmtb.com/blogImages/hero/agentic-ai-development-service.webp" />
      <meta property="og:url" content="https://www.itmtb.com/agentic-ai" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="Agentic AI Services & Solutions | ITMTB" />
      <meta name="twitter:description" content="Production agentic AI built for Indian regulatory compliance. Real outcomes from real ITMTB deployments." />
      <meta name="twitter:image" content="https://www.itmtb.com/blogImages/hero/agentic-ai-development-service.webp" />
      <link rel="canonical" href="https://www.itmtb.com/agentic-ai" />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaGraph) }}
      />
    </Head>

    {/* ── HERO ── */}
    <section className="bg-slate-950 text-white px-6 pt-36 pb-28">
      <div className="max-w-6xl mx-auto flex flex-col lg:flex-row lg:items-center lg:gap-16">
        <div className="lg:flex-1">
          <SectionEyebrow label="Agentic AI services" />
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.06] tracking-tight mb-6 max-w-3xl">
            Agentic AI services for enterprise.<br className="hidden sm:block" /> Agents in production, not pilots.
          </h1>
          <p className="text-lg sm:text-xl text-slate-400 leading-relaxed mb-4 max-w-2xl">
            We design, build, and operate production agentic AI systems — computer vision across 100,000+ SKUs in 35 countries, research agents collapsing 3-day analyses to an hour, managed services automation with 90%+ effort reduction.
          </p>
          <p className="text-base text-slate-500 mb-10 max-w-xl">
            Built for Indian regulatory compliance from the architecture phase.
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <Link href="/contact-us" legacyBehavior>
              <a className="inline-block bg-white text-slate-900 hover:bg-slate-100 px-7 py-3.5 text-sm font-semibold rounded tracking-wide transition-colors">
                Talk to us
              </a>
            </Link>
            <a
              href="#selected-work"
              className="inline-block border border-slate-700 text-slate-300 hover:border-slate-500 hover:text-white px-7 py-3.5 text-sm font-semibold rounded tracking-wide transition-colors"
            >
              See our work
            </a>
          </div>
        </div>
        <div className="hidden lg:flex lg:w-[420px] shrink-0 items-center justify-center mt-12 lg:mt-0">
          <AgentPipelineSVG />
        </div>
      </div>
    </section>

    {/* ── CUSTOMER LOGOS ── */}
    <section className="bg-white py-8 px-6">
      <CustomerSection title="Trusted by" />
    </section>

    {/* ── SELECTED WORK ── */}
    <section id="selected-work" className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Selected work" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Agents we've shipped, outcomes we've measured
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Three production deployments across supply chain, risk intelligence, and managed services.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
          {flagshipWork.map((w) => (
            <div key={w.headline} className="border border-slate-200 rounded-lg p-7 flex flex-col">
              <div className="text-4xl sm:text-5xl font-bold text-slate-900 mb-2 leading-none">{w.metric}</div>
              <h3 className="text-base font-semibold text-slate-900 mb-1 leading-snug">{w.headline}</h3>
              <p className="text-xs uppercase tracking-widest text-slate-500 mb-4">{w.descriptor}</p>
              <p className="text-sm text-slate-600 leading-relaxed mb-6 flex-1">{w.body}</p>
              {w.external ? (
                <a
                  href={w.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors w-fit"
                >
                  {w.linkLabel}
                </a>
              ) : (
                <Link href={w.link} legacyBehavior>
                  <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors w-fit">
                    {w.linkLabel}
                  </a>
                </Link>
              )}
            </div>
          ))}
        </div>
        <div className="mt-10 pt-8 border-t border-slate-100">
          <Link href="/success-stories" legacyBehavior>
            <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
              More client work
            </a>
          </Link>
        </div>
      </div>
    </section>

    {/* ── AGENTIC AI vs RPA vs ML ── */}
    <section className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <SectionEyebrow label="What is agentic AI" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Agentic AI vs RPA vs traditional ML — one screen
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Most misbuilt AI projects fail at this distinction. Here is where each category stops.
          </p>
        </div>

        <div className="overflow-x-auto mb-10">
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr className="border-b-2 border-slate-900">
                <th className="text-left py-3 pr-6 text-xs uppercase tracking-widest text-slate-500 font-semibold w-32"></th>
                <th className="text-left py-3 pr-6 text-sm font-semibold text-slate-900">RPA / Rule-based</th>
                <th className="text-left py-3 pr-6 text-sm font-semibold text-slate-900">Traditional ML</th>
                <th className="text-left py-3 text-sm font-semibold text-slate-900">Agentic AI</th>
              </tr>
            </thead>
            <tbody>
              {comparisonRows.map((row, i) => (
                <tr key={row.label} className={i < comparisonRows.length - 1 ? "border-b border-slate-200" : ""}>
                  <td className="py-4 pr-6 text-xs uppercase tracking-widest text-slate-500 font-semibold align-top">{row.label}</td>
                  <td className="py-4 pr-6 text-slate-600 align-top">{row.rpa}</td>
                  <td className="py-4 pr-6 text-slate-600 align-top">{row.ml}</td>
                  <td className="py-4 text-slate-900 font-medium align-top">{row.agentic}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-slate-200 rounded-lg p-7 max-w-3xl">
          <p className="text-slate-700 text-base leading-relaxed">
            Agentic AI systems combine an LLM reasoning engine with access to tools, APIs, and data sources. The agent plans a sequence of steps toward a goal, executes them, checks its own output, and iterates — without a human prompting each step. What distinguishes production deployments from demos is test coverage, observability, audit trails, and rollback paths built in from the start.
          </p>
          <div className="mt-4 flex flex-col items-start gap-2">
            <Link href="/blog/agentic-ai-vs-automation" legacyBehavior>
              <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
                Agentic AI vs Automation — full guide
              </a>
            </Link>
            <Link href="/blog/ai-agents-business-workflow-automation" legacyBehavior>
              <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
                Where automation breaks: putting an agent on the exceptions
              </a>
            </Link>
          </div>
        </div>
      </div>
    </section>

    {/* ── USE CASES BY INDUSTRY ── */}
    <section id="use-cases" className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="What we deploy" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Agentic AI use cases by industry
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Named workflow types we build and operate in production. Each industry has its own regulatory posture, data integration surface, and failure modes.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {industryUseCases.map((ind) => (
            <div key={ind.name} className="border border-slate-200 rounded-lg p-6 flex flex-col">
              <h3 className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-wide">{ind.name}</h3>
              <ul className="space-y-2 flex-1 mb-5">
                {ind.workflows.map((w) => (
                  <li key={w} className="text-sm text-slate-600 leading-snug pl-3 border-l border-slate-200">
                    {w}
                  </li>
                ))}
              </ul>
              {ind.link && (
                <Link href={ind.link} legacyBehavior>
                  <a className="text-xs font-semibold text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors w-fit uppercase tracking-wide">
                    Explore
                  </a>
                </Link>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── ARCHITECTURE PATTERNS ── */}
    <section className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="How we architect agents" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Four deployment patterns — and when to use each
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Picking the wrong agent architecture is the most common source of failed agentic AI projects. Here is how we evaluate each pattern before the build starts.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {architecturePatterns.map((p) => (
            <div key={p.pattern} className="bg-white border border-slate-200 rounded-lg p-7">
              <h3 className="text-base font-bold text-slate-900 mb-4">{p.pattern}</h3>
              <div className="space-y-3 mb-5">
                <div className="flex gap-3">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-0.5 w-20 flex-shrink-0">Use when</span>
                  <span className="text-sm text-slate-700 leading-snug">{p.whenToUse}</span>
                </div>
                <div className="flex gap-3">
                  <span className="text-xs uppercase tracking-widest text-slate-500 font-semibold mt-0.5 w-20 flex-shrink-0">Avoid when</span>
                  <span className="text-sm text-slate-600 leading-snug">{p.whenNotTo}</span>
                </div>
              </div>
              <div className="border-t border-slate-100 pt-4">
                <span className="text-xs uppercase tracking-widest text-amber-700 font-semibold">Our deployment — </span>
                <span className="text-xs text-slate-600">{p.example}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── PRODUCTION-GRADE REQUIREMENTS ── */}
    <section className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="What production means" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Production agents need more than a prompt
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Most agent demos are not production systems. The gap between a working demo and a monitored, compliant, rollback-capable production deployment is where most agent projects fail. Here is what we build in from the start.
          </p>
        </div>
        <div className="space-y-0">
          {productionRequirements.map((req, i) => (
            <div key={req.number} className={`flex gap-8 py-8 ${i < productionRequirements.length - 1 ? "border-b border-slate-100" : ""}`}>
              <span className="text-3xl font-bold text-slate-200 flex-shrink-0 w-12 leading-none">{req.number}</span>
              <div>
                <h3 className="text-base font-semibold text-slate-900 mb-2">{req.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed max-w-2xl">{req.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── HOW WE BUILD ── */}
    <section className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="How we build" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            A three-step engagement model
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            No open-ended retainers. Every engagement starts bounded, ships production-grade, and includes a warranty period.
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
          {methodology.map((m, i) => (
            <div key={m.step} className="flex flex-col gap-4">
              <span className="text-4xl font-bold text-slate-200 leading-none">{m.step}</span>
              <h3 className="text-base font-semibold text-slate-900">{m.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{m.body}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 pt-8 border-t border-slate-200">
          <Link href="/itmtb-how-we-work" legacyBehavior>
            <a className="text-sm font-medium text-slate-900 border-b border-transparent hover:border-slate-900 pb-px transition-colors">
              Full methodology and engagement options
            </a>
          </Link>
        </div>
      </div>
    </section>

    {/* ── HOW TO EVALUATE A PROVIDER ── */}
    <section id="how-to-evaluate" className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Choosing a provider" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            How to evaluate an agentic AI services company
          </h2>
          <p className="text-slate-600 text-base max-w-2xl">
            Most agentic AI service providers can demo an agent. Far fewer can show you one still running in
            production six months later. Six questions that separate the two.
          </p>
        </div>
        <div className="border-t border-slate-200 divide-y divide-slate-200">
          {evaluationCriteria.map((c, i) => (
            <div key={c.ask} className="py-7 flex gap-5 sm:gap-8">
              <span className="text-sm font-semibold text-slate-300 pt-1 w-6 flex-shrink-0 tabular-nums">
                {String(i + 1).padStart(2, "0")}
              </span>
              <div>
                <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">{c.ask}</h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-2">{c.why}</p>
                <p className="text-sm text-slate-900 leading-relaxed">
                  <span className="font-semibold">Ours:</span> {c.ours}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── REGULATORY POSTURE ── */}
    <section className="bg-slate-950 py-20 sm:py-28 px-6 text-white">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Regulatory posture" />
          <h2 className="text-3xl sm:text-4xl font-bold text-white leading-tight mb-3">
            Compliance built in from architecture, not retrofitted
          </h2>
          <p className="text-slate-400 text-base max-w-2xl">
            Generic AI platforms cannot carry Indian regulatory context out of the box. We architect for it from the first sprint — and where security exposure is the primary concern, we start with a{" "}
            <Link href="/services/cybersecurity" legacyBehavior>
              <a className="text-amber-500 font-medium hover:text-amber-400">cybersecurity risk assessment</a>
            </Link>
            .
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {regulatoryAnchors.map((a) => (
            <div key={a.title} className="border border-slate-800 rounded-lg p-6">
              <h3 className="text-sm font-semibold text-amber-700 mb-3">{a.title}</h3>
              <p className="text-sm text-slate-400 leading-relaxed">{a.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ── CROSS-LINK TO STRATEGY TRACK ── */}
    <div className="bg-white border-b border-slate-200">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
        <div>
          <p className="text-amber-700 text-xs font-semibold uppercase tracking-widest mb-1">For a different intent</p>
          <p className="text-slate-900 text-lg font-semibold leading-snug">Still scoping where AI fits in your business?</p>
          <p className="text-slate-500 text-sm mt-1">Two-week executive diagnostic + AI opportunity map for business leaders.</p>
        </div>
        <Link href="/ai-strategy-for-business" legacyBehavior>
          <a className="flex-shrink-0 border border-slate-300 text-slate-900 hover:bg-slate-50 px-6 py-3 text-sm font-semibold rounded transition-colors whitespace-nowrap">
            See AI Strategy for Business
          </a>
        </Link>
      </div>
    </div>

    {/* ── READINESS ASSESSMENT ── */}
    <section className="bg-slate-50 py-20 sm:py-28 px-6">
      <div className="max-w-4xl mx-auto">
        <SectionEyebrow label="Readiness assessment" />
        <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-4">
          Not sure where to start?
        </h2>
        <p className="text-slate-600 text-base leading-relaxed mb-8 max-w-2xl">
          Our readiness assessment identifies the right entry point for your organisation — which workflows are ready, which need infrastructure work first, and what a bounded pilot looks like. Six dimensions, three minutes, instant report.
        </p>
        <Link href="/tools/agentic-ai-readiness-quiz" legacyBehavior>
          <a className="inline-block bg-slate-900 text-white hover:bg-slate-800 px-7 py-3.5 text-sm font-semibold rounded tracking-wide transition-colors">
            Take the assessment
          </a>
        </Link>
      </div>
    </section>

    {/* ── INFO-SEEKER FUNNEL ── */}
    <section className="bg-white py-20 sm:py-28 px-6">
      <div className="max-w-7xl mx-auto">
        <div className="mb-12">
          <SectionEyebrow label="Learn more" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight mb-3">
            Agentic AI — depth by intent
          </h2>
          <p className="text-slate-600 text-base">Three reading paths depending on where you are starting from.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-12">
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-5">Learn the basics</h3>
            <ul className="space-y-4">
              {infoBlogs.basics.map((b) => (
                <li key={b.slug}>
                  <Link href={`/blog/${b.slug}`} legacyBehavior>
                    <a className="text-sm font-medium text-slate-900 leading-snug border-b border-transparent hover:border-slate-900 pb-px transition-colors">
                      {b.title}
                    </a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-5">Get industry-specific</h3>
            <ul className="space-y-4">
              {infoBlogs.industry.map((b) => (
                <li key={b.slug}>
                  <Link href={`/blog/${b.slug}`} legacyBehavior>
                    <a className="text-sm font-medium text-slate-900 leading-snug border-b border-transparent hover:border-slate-900 pb-px transition-colors">
                      {b.title}
                    </a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <h3 className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-5">Our approach</h3>
            <ul className="space-y-4">
              {infoBlogs.approach.map((b) => (
                <li key={b.slug}>
                  <Link href={`/blog/${b.slug}`} legacyBehavior>
                    <a className="text-sm font-medium text-slate-900 leading-snug border-b border-transparent hover:border-slate-900 pb-px transition-colors">
                      {b.title}
                    </a>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    </section>

    {/* ── BOOKING CTA ── */}
    <div className="bg-white">
      <ContactUsHome />
    </div>

    {/* ── FAQ ── */}
    <section className="bg-white py-20 sm:py-28 px-6 border-t border-slate-100">
      <div className="max-w-4xl mx-auto">
        <div className="mb-10">
          <SectionEyebrow label="FAQ" />
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 leading-tight">
            Frequently asked questions
          </h2>
        </div>
        <FaqAccordion items={faqItems} />
      </div>
    </section>

    {/* ── RELATED BLOGS ── */}
    <section className="bg-white py-16 px-6 border-t border-slate-100">
      <div className="max-w-7xl mx-auto">
        <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-10">More on agentic AI</h2>
        <BlogSlider blogs={relatedBlogs} />
      </div>
    </section>

    <Footer />
  </div>
);

AiAgentHomePage.title = "Agentic AI for Enterprise | ITMTB";
AiAgentHomePage.desc = "ITMTB builds agentic AI systems for banks, insurers, life sciences, retail, and supply chain — from pilot to production.";

export default AiAgentHomePage;
